import { Link } from "react-router-dom";
import { Leaf, Instagram, Youtube, Twitter } from "lucide-react";

const FOOTER_BANNER =
  "https://static.prod-images.emergentagent.com/jobs/3112c7fc-8e98-4a3a-b5ca-162acb16129b/images/7891ba039b1f6058a766f50da0187513d711f24411364514cd9685fea62c6fb5.png";

const COLS = [
  {
    title: "Shop",
    links: [
      { label: "All Plants", to: "/shop" },
      { label: "Bonsai", to: "/shop/bonsai" },
      { label: "Succulents", to: "/shop/succulents" },
      { label: "Terrariums", to: "/shop/terrariums" },
      { label: "Accessories", to: "/shop/accessories" },
    ],
  },
  {
    title: "Community",
    links: [
      { label: "Join Skool — $50/mo", to: "/community" },
      { label: "Workshops", to: "/community" },
      { label: "Members area", to: "/community" },
    ],
  },
  {
    title: "Journal",
    links: [
      { label: "Kenny's notes", to: "/about" },
      { label: "Care guides", to: "/about" },
      { label: "Coastal field log", to: "/about" },
    ],
  },
  {
    title: "Contact",
    links: [
      { label: "About Kenny", to: "/about" },
      { label: "Wholesale", to: "/about" },
      { label: "Press", to: "/about" },
    ],
  },
];

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="mt-24 bg-[#3D3A33] text-[#F5EFE2]"
    >
      {/* Slim coastal-dawn banner above the footer columns */}
      <div
        data-testid="footer-banner"
        className="relative h-[200px] overflow-hidden border-y border-[#5E594E]/70"
      >
        <img
          src={FOOTER_BANNER}
          alt="Coastal dawn on the Pacific shore"
          className="h-full w-full object-cover"
          loading="lazy"
        />
        {/* Top fade so the page → banner transition is clean */}
        <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#3D3A33] to-transparent" />
        {/* Bottom fade into the footer column block */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-[#3D3A33] to-transparent" />
        <div className="absolute inset-0 flex items-center justify-center px-6">
          <p className="font-serif text-xl italic tracking-wide text-[#F5EFE2] drop-shadow sm:text-2xl">
            Grow something real.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-6 py-16">
        {/* Centered brand mark + tagline above the four-column nav */}
        <div className="mb-12 flex flex-col items-center gap-3 text-center">
          <Link to="/" data-testid="footer-brand-mark" aria-label="Coastal Roots Botanica home">
            <img
              src="/logo-transparent.png"
              alt="Coastal Roots Botanica logo"
              className="h-16 w-16"
              width={64}
              height={64}
            />
          </Link>
          <p className="text-xs uppercase tracking-[0.24em] text-[#C9A961]">
            Pacific Coast Workshop · Est. 2026
          </p>
        </div>

        <div className="grid gap-12 md:grid-cols-[1.4fr_repeat(4,1fr)]">
          <div className="space-y-4">
            <Link to="/" className="flex items-center gap-3" data-testid="footer-logo">
              <img
                src="/logo-transparent.png"
                alt=""
                className="h-8 w-8"
                width={32}
                height={32}
                aria-hidden="true"
              />
              <span className="font-serif text-2xl tracking-wide text-[#F5EFE2]">
                Coastal Roots Botanica
              </span>
            </Link>
            <p className="max-w-sm text-sm text-[#C9BEA8]">
              Bonsai, succulents, and custom terrariums shaped by hand on the
              Pacific coast. Slow, deliberate, hands in the soil.
            </p>
            <div className="flex items-center gap-3 pt-2">
              <a
                href="#"
                className="rounded-full border border-[#5E594E] p-2 text-[#C9BEA8] transition-colors hover:border-[#C9A961]/60 hover:bg-[#C9A961]/10 hover:text-[#C9A961]"
                data-testid="footer-instagram"
                aria-label="Instagram"
              >
                <Instagram className="h-4 w-4" />
              </a>
              <a
                href="#"
                className="rounded-full border border-[#5E594E] p-2 text-[#C9BEA8] transition-colors hover:border-[#C9A961]/60 hover:bg-[#C9A961]/10 hover:text-[#C9A961]"
                data-testid="footer-youtube"
                aria-label="YouTube"
              >
                <Youtube className="h-4 w-4" />
              </a>
              <a
                href="#"
                className="rounded-full border border-[#5E594E] p-2 text-[#C9BEA8] transition-colors hover:border-[#C9A961]/60 hover:bg-[#C9A961]/10 hover:text-[#C9A961]"
                data-testid="footer-twitter"
                aria-label="Twitter / X"
              >
                <Twitter className="h-4 w-4" />
              </a>
            </div>
          </div>
          {COLS.map((col) => (
            <div key={col.title} className="space-y-3">
              <p className="text-xs uppercase tracking-[0.18em] text-[#C9A961]">
                {col.title}
              </p>
              <ul className="space-y-2">
                {col.links.map((link) => (
                  <li key={link.label}>
                    <Link
                      to={link.to}
                      className="text-sm text-[#F5EFE2]/85 transition-colors hover:text-[#C9A961]"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Wordmark + tagline strip per Phase 1.6 spec */}
        <div
          data-testid="footer-wordmark"
          className="mt-12 flex flex-col items-start justify-between gap-3 border-t border-[#5E594E]/70 pt-6 text-xs text-[#C9BEA8] sm:flex-row sm:items-center"
        >
          <p className="font-serif tracking-wide">
            Coastal Roots Botanica · Pacific Coast Workshop · Est. 2026
          </p>
          <p>© {new Date().getFullYear()} · Made with patience on the coast.</p>
        </div>
      </div>
    </footer>
  );
}
